import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, X } from 'lucide-react';
import { useUploadStore } from '../store/uploadStore';
import { useBookUploadStore } from '../store/bookUploadStore';

// A small app-level tray, visible across pages while uploads run, so the user
// can keep browsing (D8) — and, for a large book batch, trust it's still
// working without staying on the Upload page. Video and book batches are
// tracked in separate stores; this shows whichever currently has items,
// preferring video if (implausibly) both are active, since only one upload
// surface is normally in use at a time.
export function UploadIndicator() {
  const navigate = useNavigate();
  const videoItems = useUploadStore((s) => s.items);
  const activeGroupId = useUploadStore((s) => s.activeGroupId);
  const clearVideoSettled = useUploadStore((s) => s.clearSettled);
  const bookItems = useBookUploadStore((s) => s.items);
  const clearBookSettled = useBookUploadStore((s) => s.clearSettled);

  const kind: 'video' | 'book' | null =
    videoItems.length > 0 ? 'video' : bookItems.length > 0 ? 'book' : null;

  if (kind === null) return null;

  const items = kind === 'video' ? videoItems : bookItems;
  const clearSettled = kind === 'video' ? clearVideoSettled : clearBookSettled;

  const inFlight = items.filter(
    (i) => i.status === 'uploading' || i.status === 'processing' || i.status === 'queued'
  ).length;
  const done = items.filter((i) => i.status === 'done' || i.status === 'duplicate').length;
  const failed = items.filter((i) => i.status === 'failed').length;
  const allSettled = inFlight === 0;

  const label = allSettled
    ? `${done} uploaded${failed ? `, ${failed} failed` : ''}`
    : `Uploading ${done}/${items.length}…`;

  const go = () => {
    if (kind === 'video') {
      if (activeGroupId !== null) navigate(`/video-groups/${activeGroupId}`);
    } else {
      navigate('/upload');
    }
  };

  const goDisabled = kind === 'video' && activeGroupId === null;

  return (
    <div className={`upload-indicator ${allSettled ? 'upload-indicator--done' : ''}`} role="status" aria-live="polite">
      <button className="upload-indicator-main" onClick={go} disabled={goDisabled}>
        {allSettled ? (
          <CheckCircle2 className="icon icon-sm" aria-hidden="true" />
        ) : (
          <Loader2 className="icon icon-sm upload-indicator-spin" aria-hidden="true" />
        )}
        <span>{label}</span>
      </button>
      {allSettled && (
        <button className="upload-indicator-dismiss" onClick={clearSettled} aria-label="Dismiss">
          <X className="icon icon-sm" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
